import React, { type ReactNode, useCallback } from 'react';

import { ErrorBoundary } from 'react-error-boundary';

import type { PopupProps } from '@atlaskit/popup';
import { layers } from '@atlaskit/theme/constants';

import {
	entryPoint,
	type UserProfileCardRelayTriggerParams,
} from '../network/userProfileCard.entrypoint';
import { PopupTriggerWithHover, type TriggerMode } from '../popup-adapter/PopupTriggerWithHover';
import { getPageTime } from '../utils/performance';

import { CardError } from './components/CardError';
import { CardLoading } from './components/CardLoading';
import { UserProfileCardTrigger } from './UserProfileCardTrigger';

export type UserProfileCardWithTriggerProps = UserProfileCardRelayTriggerParams &
	Pick<PopupProps, 'placement'> & {
		/** When omitted, defaults to `'click'`. */
		trigger?: TriggerMode;
		shouldRenderToParent?: boolean;
		children: ReactNode;
		testId?: string;
	};

/**
 * Opens the user profile card in a popup (hover / click / preload) using the same Relay entry-point
 * pattern as `TeamProfileCardWithTrigger` (`@atlassian/team-profilecard/trigger`): preload runs
 * `UserProfileCardEntryPointQuery` (`knowledgeDiscovery.singleUser` + `activity.workedOn`).
 *
 * For a presentational card with data you already have, use {@link UserProfileCard} directly.
 */
export function UserProfileCardWithTrigger({
	accountId,
	cloudId,
	orgId,
	requestingUserId,
	trigger,
	placement = 'bottom-start',
	shouldRenderToParent = false,
	children,
	testId,
}: UserProfileCardWithTriggerProps): React.JSX.Element {
	const resolvedTriggerMode = trigger ?? 'click';

	const onErrorBoundary = useCallback(() => {
		void getPageTime();
	}, []);

	// TODO update to use error and onClose props from the error boundary, once CardError is updated to accept these props
	// needed by the type of the InternalEntryPointContainer's errorFallback prop
	const entryPointErrorFallback = useCallback(
		(_props: { error: Error; onClose: () => void }) => <CardError testId={testId} />,
		[testId],
	);

	if (!accountId || !cloudId || !requestingUserId) {
		void getPageTime();
		return <>{children}</>;
	}

	return (
		<ErrorBoundary onError={onErrorBoundary} fallback={<>{children}</>}>
			<PopupTriggerWithHover
				entryPoint={entryPoint}
				placement={placement}
				fallback={<CardLoading />}
				errorFallback={entryPointErrorFallback}
				entryPointParams={{ accountId, cloudId, orgId, requestingUserId }}
				entryPointProps={{
					cloudId,
					testId,
				}}
				shouldRenderToParent={shouldRenderToParent}
				shouldUseCaptureOnOutsideClick={true}
				zIndex={layers.modal()}
				triggerMode={resolvedTriggerMode}
				trigger={(triggerProps) => (
					<UserProfileCardTrigger trigger={resolvedTriggerMode} triggerProps={triggerProps}>
						{children}
					</UserProfileCardTrigger>
				)}
			/>
		</ErrorBoundary>
	);
}
